"use client";

import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ComponentProps, ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { cn } from "@/lib/utils";
import { useIsPhone } from "@/hooks/useIsPhone";
import { getStagedWindow, useDesktopStore } from "@/stores/desktopStore";
import { DESKTOP_Z_INDEX, DOCK_LAYOUT, ISLAND_LAYOUT } from "./desktopUI.constants";
import { useIslandMotion } from "./useIslandMotion";
import type { SpotlightRequest } from "@/components/GlobalSearchDialog";
import { AskRow, Button, Glyph, Keycap, Tooltip, TopBarDivider, TopBarIsland } from "@/components/desktop/ui";
import { useAnchoredOverlay } from "@/components/desktop/ui/useAnchoredOverlay";
import { createLazyOverlay } from "@/components/desktop/ui/createLazyOverlay";
import type { QuickAsk as QuickAskComponent } from "./QuickAsk";
import { useWaitingAnswer } from "./useWaitingAnswer";
import { WILSON_BAR_TRANSITION } from "./viewTransition.utils";
import { PaneShownContext } from "@/components/desktop/ui/usePaneBack";
import { AskPanelBottomContext, IslandWidthContext } from "@/components/desktop/ui/islandLane.utils";
import { TopBarContext } from "./TopBarContext";
import { useSideChatInset } from "./useSideChatInset";
import { useSideChatStore } from "@/stores/sideChatStore";
type TopBarSlotName = "back" | "left" | "leftMeta" | "center" | "right" | "controls";
type SlotClaims = Record<TopBarSlotName, number>;
export const PageStagedContext = createContext(true);
const EMPTY_SLOTS: Record<TopBarSlotName, HTMLElement | null> = {
  back: null,
  left: null,
  leftMeta: null,
  center: null,
  right: null,
  controls: null
};
const NO_CLAIMS: SlotClaims = {
  back: 0,
  left: 0,
  leftMeta: 0,
  center: 0,
  right: 0,
  controls: 0
};
export function TopBarProvider({
  children,
  onOpenSearch,
  onOpenSpotlight,
  isSpotlightOpen = false,
  isSpotlightSearching = false
}: {
  children: ReactNode;
  onOpenSearch: () => void;
  onOpenSpotlight?: (request: SpotlightRequest) => void;
  isSpotlightOpen?: boolean;
  isSpotlightSearching?: boolean;
}) {
  const [slots, setSlots] = useState(EMPTY_SLOTS);
  const [claims, setClaims] = useState(NO_CLAIMS);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isCenterQueryActive, setIsCenterQueryActive] = useState(false);
  const [isSearchDismissed, setIsSearchDismissed] = useState(false);
  const [centerSearchHint, setCenterSearchHint] = useState("");
  const [wilsonSubject, setWilsonSubject] = useState("");
  const [islandWidth, setIslandWidth] = useState(0);
  const [askPanelBottom, setAskPanelBottom] = useState(0);
  const [isAsking, setIsAsking] = useState(false);
  const [askingQuestion, setAskingQuestion] = useState<string>();
  const isPhone = useIsPhone();
  const centerOnAsked = useRef<(() => void) | undefined>(undefined);
  const setShouldKeepCenterOpen = useCallback((shouldKeepOpen: boolean) => {
    setIsCenterQueryActive(shouldKeepOpen);
    setIsSearchDismissed(false);
  }, []);
  const shouldKeepCenterOpen = isCenterQueryActive && !isSearchDismissed;
  const registerSlot = useCallback((name: TopBarSlotName, el: HTMLElement | null) => setSlots(prev => prev[name] === el ? prev : {
    ...prev,
    [name]: el
  }), []);
  const claimSlot = useCallback((name: TopBarSlotName) => {
    setClaims(prev => ({
      ...prev,
      [name]: prev[name] + 1
    }));
    return () => setClaims(prev => ({
      ...prev,
      [name]: prev[name] - 1
    }));
  }, []);
  const expandSearch = useCallback(() => {
    setIsSearchExpanded(true);
    setIsSearchDismissed(false);
  }, []);
  const collapseSearch = useCallback(() => setIsSearchExpanded(false), []);
  const dismissSearch = useCallback(() => {
    setIsSearchExpanded(false);
    setIsSearchDismissed(true);
  }, []);
  const hasPageSearch = claims.center > 0;
  useEffect(() => {
    if (!hasPageSearch) setIsSearchExpanded(false);
  }, [hasPageSearch]);
  const hasStagedWindow = useDesktopStore(state => !!getStagedWindow(state.windows));
  const hasPlace = hasStagedWindow || claims.back + claims.left + claims.leftMeta > 0;
  const isQuickAskAvailable = !isPhone;
  const {
    waitingAnswer,
    dismiss: dismissWaitingAnswer
  } = useWaitingAnswer({
    isEnabled: isQuickAskAvailable && !isSpotlightOpen
  });
  const [isResuming, setIsResuming] = useState(false);
  const isResumingRef = useRef(false);
  const beginAsking = useCallback((question: string | undefined, isResumingAsk: boolean) => {
    setIsSearchExpanded(false);
    setAskingQuestion(question);
    isResumingRef.current = isResumingAsk;
    setIsResuming(isResumingAsk);
    setIsAsking(true);
  }, []);
  const startAsking = useCallback((question?: string) => beginAsking(question, false), [beginAsking]);
  const openWaitingAnswer = useCallback(() => beginAsking(undefined, true), [beginAsking]);
  const stopAsking = useCallback(() => {
    setIsAsking(false);
    if (isResumingRef.current) dismissWaitingAnswer();
    isResumingRef.current = false;
    setIsResuming(false);
  }, [dismissWaitingAnswer]);
  const openSpotlight = useCallback((request: SpotlightRequest) => {
    if (onOpenSpotlight) onOpenSpotlight(request);else onOpenSearch();
  }, [onOpenSpotlight, onOpenSearch]);
  useEffect(() => {
    if (!isQuickAskAvailable || isSpotlightOpen) {
      setIsAsking(false);
      isResumingRef.current = false;
      setIsResuming(false);
    }
  }, [isQuickAskAvailable, isSpotlightOpen]);
  useEffect(() => {
    if (!hasPageSearch && !hasPlace || isSpotlightOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key === "f" && hasPageSearch) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setIsSearchExpanded(true);
        setIsSearchDismissed(false);
        return;
      }
      if (event.key === "k" && hasPlace && isPhone && onOpenSpotlight) {
        event.preventDefault();
        event.stopImmediatePropagation();
        onOpenSpotlight({
          isChat: true
        });
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [hasPageSearch, hasPlace, isPhone, isSpotlightOpen, onOpenSpotlight]);
  const value = useMemo(() => ({
    slots,
    registerSlot,
    claimSlot,
    claims,
    openSearch: onOpenSearch,
    openSpotlight,
    isSpotlightOpen,
    isSpotlightSearching,
    isSearchExpanded,
    expandSearch,
    collapseSearch,
    dismissSearch,
    setShouldKeepCenterOpen,
    shouldKeepCenterOpen,
    setCenterSearchHint,
    centerSearchHint,
    setWilsonSubject,
    wilsonSubject,
    centerOnAsked,
    setIslandWidth,
    setAskPanelBottom,
    isAsking,
    askingQuestion,
    isQuickAskAvailable,
    startAsking,
    stopAsking,
    waitingAnswer,
    isResuming,
    openWaitingAnswer
  }), [slots, registerSlot, claimSlot, claims, onOpenSearch, openSpotlight, isSpotlightOpen, isSpotlightSearching, isSearchExpanded, expandSearch, collapseSearch, dismissSearch, setShouldKeepCenterOpen, shouldKeepCenterOpen, centerSearchHint, wilsonSubject, isAsking, askingQuestion, isQuickAskAvailable, startAsking, stopAsking, waitingAnswer, isResuming, openWaitingAnswer]);
  return <TopBarContext.Provider value={value} data-sentry-element="TopBarContext.Provider" data-sentry-component="TopBarProvider" data-sentry-source-file="TopBar.tsx">
            <IslandWidthContext.Provider value={islandWidth} data-sentry-element="IslandWidthContext.Provider" data-sentry-source-file="TopBar.tsx">
                <AskPanelBottomContext.Provider value={askPanelBottom} data-sentry-element="AskPanelBottomContext.Provider" data-sentry-source-file="TopBar.tsx">
                    {children}
                </AskPanelBottomContext.Provider>
            </IslandWidthContext.Provider>
        </TopBarContext.Provider>;
}
const QuickAsk = createLazyOverlay<ComponentProps<typeof QuickAskComponent>>({
  name: "quickAsk",
  load: () => import("./QuickAsk"),
  exportName: "QuickAsk",
  renderFallback: () => null
});
export function useOpenSearch(): () => void {
  const ctx = useContext(TopBarContext);
  return ctx?.openSearch ?? noop;
}
const noop = () => {};

/**
 * The island: an ink pill at the top center, over the page. At rest it says
 * where you are and holds the page's actions; search grows it into a field
 * (the page's own search, or Spotlight when the page has none). The page in
 * front fills it through `TopBarSlot`. It wears the dark tokens, so whatever
 * a page puts in it reads on ink without styling of its own.
 */
const ISLAND_STYLE = {
  top: ISLAND_LAYOUT.TOP,
  height: ISLAND_LAYOUT.HEIGHT,
  zIndex: DESKTOP_Z_INDEX.DOCK
};
const ISLAND_CENTERED = "left-1/2 max-w-[calc(100vw-32px)] -translate-x-1/2 justify-center";
const ISLAND_CONTENT = "relative flex items-center gap-1";
const PHONE_NAV_BAR = "left-4";
const PHONE_EDGE = 16;
const PHONE_GAP = 8;
const ISLAND_CONTENT_CENTERED = "max-w-[calc(100vw-40px)] flex-shrink-0";
const BARE_SEARCH = "pl-3 pr-1.5 touch:pr-3";
const BARE_SEARCH_TEXT = "text-callout";
export function TopBar() {
  const t = useClientTranslations("search");
  const tPage = useClientTranslations("desktop.page");
  const ctx = useContext(TopBarContext);
  const isPhone = useIsPhone();
  const registerSlot = ctx?.registerSlot;
  const slotRefs = useMemo(() => ({
    back: (el: HTMLElement | null) => registerSlot?.("back", el),
    left: (el: HTMLElement | null) => registerSlot?.("left", el),
    leftMeta: (el: HTMLElement | null) => registerSlot?.("leftMeta", el),
    center: (el: HTMLElement | null) => registerSlot?.("center", el),
    right: (el: HTMLElement | null) => registerSlot?.("right", el),
    controls: (el: HTMLElement | null) => registerSlot?.("controls", el)
  }), [registerSlot]);
  const showDesktop = useDesktopStore(state => state.showDesktop);
  const sideChatInset = useSideChatInset();
  const askButtonWidth = useSideChatStore(state => state.askButtonWidth);
  const hasStagedWindow = useDesktopStore(state => !!getStagedWindow(state.windows));
  const centerEl = ctx?.slots.center;
  const centerId = useId();
  const isAsking = !!ctx?.isAsking;
  const isOpen = !isAsking && !!ctx && ctx.claims.center > 0 && (ctx.isSearchExpanded || ctx.shouldKeepCenterOpen);
  const [pageQuery, setPageQuery] = useState("");
  useEffect(() => {
    if (!isOpen || !centerEl) {
      setPageQuery("");
      return;
    }
    let frame = 0;
    const read = () => setPageQuery(centerEl.querySelector("input")?.value ?? "");
    const readAfterRender = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(read);
    };
    read();
    centerEl.addEventListener("input", read);
    centerEl.addEventListener("click", readAfterRender);
    return () => {
      cancelAnimationFrame(frame);
      centerEl.removeEventListener("input", read);
      centerEl.removeEventListener("click", readAfterRender);
    };
  }, [isOpen, centerEl]);
  const pageQuestion = isOpen ? pageQuery.trim() : "";
  const {
    triggerRef: askAnchorRef,
    panelRef: askRef,
    style: askStyle
  } = useAnchoredOverlay<HTMLElement, HTMLDivElement>({
    isOpen: !!pageQuestion,
    align: "center",
    gap: 8
  });
  useReportAskPanelBottom({
    anchorRef: askAnchorRef,
    panelRef: askRef,
    isOpen: !!pageQuestion,
    isPhone,
    gap: 8,
    setAskPanelBottom: ctx?.setAskPanelBottom
  });
  const openSpotlight = ctx?.openSpotlight;
  const askAboutPageQuery = useCallback(() => {
    const asked = centerEl?.querySelector("input")?.value.trim();
    if (!asked || !openSpotlight) return;
    ctx?.dismissSearch();
    ctx?.centerOnAsked.current?.();
    openSpotlight({
      question: asked
    });
  }, [centerEl, openSpotlight, ctx]);
  const isSearchExpanded = ctx?.isSearchExpanded;
  useEffect(() => {
    if (!isSearchExpanded || !centerEl) return;
    centerEl.querySelector("input")?.focus();
  }, [isSearchExpanded, centerEl]);
  const headerRef = useRef<HTMLElement>(null);
  const hasPlace = hasStagedWindow || !!ctx && (ctx.claims.back > 0 || ctx.claims.left > 0 || ctx.claims.leftMeta > 0);
  const contentRef = useRef<HTMLDivElement>(null);
  useIslandMotion({
    islandRef: headerRef,
    contentRef
  });
  useReportIslandWidth({
    islandRef: headerRef,
    contentRef,
    isSearchOpen: isOpen,
    setIslandWidth: ctx?.setIslandWidth
  });
  const setHeaderRef = useCallback((el: HTMLElement | null) => {
    headerRef.current = el;
    askAnchorRef.current = el;
  }, [askAnchorRef]);
  const collapseSearch = ctx?.collapseSearch;
  const expandSearch = ctx?.expandSearch;
  const dismissSearch = ctx?.dismissSearch;
  useEffect(() => {
    const header = headerRef.current;
    if (!header || !collapseSearch || !expandSearch || !dismissSearch) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && isOpen) {
        event.preventDefault();
        askAboutPageQuery();
        return;
      }
      if (event.key !== "Escape" || !isOpen) return;
      event.preventDefault();
      dismissSearch();
      if (event.target instanceof HTMLElement) event.target.blur();
    };
    const onFocusOut = (event: FocusEvent) => {
      const left = event.target;
      if (!(left instanceof Node) || !centerEl?.contains(left)) return;
      const next = event.relatedTarget;
      if (!(next instanceof Node) || !header.contains(next)) {
        collapseSearch();
      }
    };
    const onFocusIn = (event: FocusEvent) => {
      const entered = event.target;
      if (entered instanceof Node && centerEl?.contains(entered)) {
        expandSearch();
      }
    };
    header.addEventListener("keydown", onKeyDown);
    header.addEventListener("focusout", onFocusOut);
    header.addEventListener("focusin", onFocusIn);
    return () => {
      header.removeEventListener("focusin", onFocusIn);
      header.removeEventListener("keydown", onKeyDown);
      header.removeEventListener("focusout", onFocusOut);
    };
  }, [isOpen, collapseSearch, expandSearch, dismissSearch, centerEl, askAboutPageQuery]);
  if (!ctx) return null;
  const {
    claims,
    openSearch
  } = ctx;
  const hasChip = claims.left > 0;
  const isNavBar = isPhone && hasPlace;
  const isHidden = isOpen || isAsking;
  const isHome = !hasPlace && !isPhone;
  const islandStyle = isNavBar ? {
    ...ISLAND_STYLE,
    right: PHONE_EDGE + (askButtonWidth > 0 ? askButtonWidth + PHONE_GAP : 0)
  } : hasPlace && sideChatInset > 0 ? {
    ...ISLAND_STYLE,
    left: `calc(50% - ${sideChatInset / 2}px)`
  } : ISLAND_STYLE;
  return <TopBarIsland style={islandStyle} ref={setHeaderRef} className={cn("fixed", !ctx.isSpotlightOpen && WILSON_BAR_TRANSITION, "data-[attached=true]:rounded-b-none data-[attached=true]:rounded-t-panel", isNavBar ? PHONE_NAV_BAR : ISLAND_CENTERED)} data-sentry-element="TopBarIsland" data-sentry-component="TopBar" data-sentry-source-file="TopBar.tsx">
            <div ref={contentRef} className={cn(ISLAND_CONTENT, isNavBar ? "w-full min-w-0" : ISLAND_CONTENT_CENTERED)}>
                {!isHidden && hasPlace && !isNavBar && <>
                        <Tooltip side="bottom" content={tPage("desktop")}>
                            <Button variant="control" size="icon" onClick={showDesktop} aria-label={tPage("desktop")}>
                                <Glyph name="home" />
                            </Button>
                        </Tooltip>
                        <TopBarDivider />
                    </>}
                <div ref={slotRefs.back} className={cn("flex flex-shrink-0 items-center", (isHidden || claims.back === 0) && "hidden")} />
                {!isHidden && claims.back > 0 && hasChip && <TopBarDivider />}
                <div className={cn("flex min-w-0 items-center", isNavBar ? "flex-shrink" : "max-w-[40vw]", (isHidden || !hasChip) && "hidden")}>
                    <div ref={slotRefs.left} className="flex min-w-0 items-center gap-2" />
                </div>
                <div ref={slotRefs.leftMeta} className={cn("light-tokens flex flex-shrink-0 items-center", (isHidden || claims.leftMeta === 0 || isNavBar) && "hidden")} />
                {isNavBar && !isOpen && <span className="min-w-0 flex-1" aria-hidden />}
                <div ref={slotRefs.center} id={centerId} className={cn("items-center pl-1", isOpen ? isNavBar ? "flex min-w-0 flex-1" : "flex w-[min(520px,56vw)]" : "hidden")} />
                <div ref={slotRefs.right} className={cn("flex items-center gap-0.5", (isAsking || claims.right === 0) && "hidden")} />
                <div ref={slotRefs.controls} className={cn("flex items-center gap-0.5", (isAsking || claims.controls === 0 || isNavBar) && "hidden")} />
                {isAsking && <QuickAsk anchorRef={headerRef} subject={hasPlace ? ctx.wilsonSubject : ""} initialQuestion={ctx.askingQuestion} resumed={ctx.isResuming ? ctx.waitingAnswer : null} onClose={ctx.stopAsking} openSpotlight={ctx.openSpotlight} bottomReserve={isHome ? DOCK_LAYOUT.HEIGHT + 40 : ISLAND_LAYOUT.TOP * 2} />}
                {!isHidden && <>
                        {hasPlace && <TopBarDivider />}
                        <Tooltip side="bottom" content={<span className="flex items-center gap-1.5">
                                    {t("searchLabel")}
                                    <Keycap>
                                        {claims.center > 0 ? "⌘F" : "⌘K"}
                                    </Keycap>
                                </span>}>
                            <Button variant="control" size={isNavBar ? "icon" : "sm"} onClick={claims.center > 0 ? expandSearch : openSearch} aria-label={ctx.centerSearchHint || t("searchLabel")} aria-keyshortcuts={claims.center > 0 ? "Meta+F" : "Meta+K"} aria-expanded={claims.center > 0 ? isOpen : undefined} aria-controls={claims.center > 0 ? centerId : undefined} className={cn("font-normal", !isNavBar && BARE_SEARCH)}>
                                <Glyph name="search" />
                                {!isNavBar && <>
                                        <span className={BARE_SEARCH_TEXT}>
                                            {t("searchLabel")}
                                        </span>
                                        {!isPhone && <Keycap>
                                                {claims.center > 0 ? "⌘F" : "⌘K"}
                                            </Keycap>}
                                    </>}
                            </Button>
                        </Tooltip>
                    </>}
            </div>
            {pageQuestion && typeof document !== "undefined" && createPortal(<div ref={askRef} role="listbox" aria-label={t("askLabel")} style={{
      ...askStyle,
      zIndex: DESKTOP_Z_INDEX.OVERLAY
    }} className="w-[min(520px,calc(100vw-32px))] rounded-panel border border-[var(--glass-border)] bg-[var(--glass-bg)] p-1.5 shadow-glass backdrop-blur-[20px]">
                        <AskRow shortcut="⌘↵" onSelect={askAboutPageQuery}>
                            {t("askInsteadOf", {
          query: pageQuestion
        })}
                        </AskRow>
                    </div>, document.body)}
        </TopBarIsland>;
}
function useReportIslandWidth({
  islandRef,
  contentRef,
  isSearchOpen,
  setIslandWidth
}: {
  islandRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  isSearchOpen: boolean;
  setIslandWidth: ((width: number) => void) | undefined;
}) {
  useEffect(() => {
    const island = islandRef.current;
    const content = contentRef.current;
    if (!island || !content || !setIslandWidth || isSearchOpen || typeof ResizeObserver !== "function" || typeof getComputedStyle !== "function") {
      return;
    }
    const report = () => {
      const {
        paddingLeft,
        paddingRight
      } = getComputedStyle(island);
      setIslandWidth(Math.round(content.offsetWidth + parseFloat(paddingLeft) + parseFloat(paddingRight)));
    };
    report();
    const observer = new ResizeObserver(report);
    observer.observe(content);
    return () => observer.disconnect();
  }, [islandRef, contentRef, isSearchOpen, setIslandWidth]);
}
function useReportAskPanelBottom({
  anchorRef,
  panelRef,
  isOpen,
  isPhone,
  gap,
  setAskPanelBottom
}: {
  anchorRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  isPhone: boolean;
  gap: number;
  setAskPanelBottom: ((bottom: number) => void) | undefined;
}) {
  useEffect(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!setAskPanelBottom) return;
    if (!isOpen || !anchor || !panel) {
      setAskPanelBottom(0);
      return;
    }
    const report = () => setAskPanelBottom(Math.round(anchor.getBoundingClientRect().bottom + gap + panel.offsetHeight));
    report();
    window.addEventListener("resize", report);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(report) : undefined;
    observer?.observe(anchor);
    observer?.observe(panel);
    return () => {
      window.removeEventListener("resize", report);
      observer?.disconnect();
      setAskPanelBottom(0);
    };
  }, [anchorRef, panelRef, isOpen, isPhone, gap, setAskPanelBottom]);
}
export function TopBarSlot({
  slot,
  shouldKeepOpen = false,
  searchHint = "",
  subject,
  onAsked,
  children
}: {
  slot: TopBarSlotName;
  shouldKeepOpen?: boolean;
  searchHint?: string;
  subject?: string;
  /** The center slot's words were just sent to Wilson as a question. */
  onAsked?: () => void;
  children: ReactNode;
}) {
  const ctx = useContext(TopBarContext);
  const isPageStaged = useContext(PageStagedContext);
  const isPaneShown = useContext(PaneShownContext);
  const isStaged = isPageStaged && isPaneShown;
  const claimSlot = ctx?.claimSlot;
  const setShouldKeepCenterOpen = ctx?.setShouldKeepCenterOpen;
  useEffect(() => {
    if (!isStaged || !claimSlot) return;
    return claimSlot(slot);
  }, [slot, isStaged, claimSlot]);
  useEffect(() => {
    if (slot !== "center" || !isStaged || !setShouldKeepCenterOpen) return;
    setShouldKeepCenterOpen(shouldKeepOpen);
    return () => setShouldKeepCenterOpen(false);
  }, [slot, isStaged, shouldKeepOpen, setShouldKeepCenterOpen]);
  const setCenterSearchHint = ctx?.setCenterSearchHint;
  useEffect(() => {
    if (slot !== "center" || !isStaged || !setCenterSearchHint) return;
    setCenterSearchHint(searchHint);
    return () => setCenterSearchHint("");
  }, [slot, isStaged, searchHint, setCenterSearchHint]);
  const setWilsonSubject = ctx?.setWilsonSubject;
  useEffect(() => {
    if (slot !== "left" || !isStaged || !setWilsonSubject) return;
    setWilsonSubject(subject ?? "");
    return () => setWilsonSubject("");
  }, [slot, isStaged, subject, setWilsonSubject]);
  const centerOnAsked = ctx?.centerOnAsked;
  useEffect(() => {
    if (slot !== "center" || !isStaged || !centerOnAsked) return;
    centerOnAsked.current = onAsked;
    return () => {
      centerOnAsked.current = undefined;
    };
  }, [slot, isStaged, onAsked, centerOnAsked]);
  const el = ctx?.slots[slot];
  if (!isStaged || !el) return null;
  return createPortal(children, el);
}